import { BaseCommand, createTable } from "@crunchdao/shared-cli";
import { formatCrunch } from "@crunchdao/sdk";
import chalk from "chalk";

export class ListPositionsAccountsCommand extends BaseCommand {
  async execute(): Promise<void> {
    try {
      // Get all account infos from the staking program
      const programId = this.stakingProgram.programId;
      const allAccounts = await this.provider.connection.getProgramAccounts(programId);

      const positionsAccounts = [];
      const failedAccounts = [];

      // Try to decode each account as StakingPositions
      for (const accountInfo of allAccounts) {
        try {
          const decoded = this.stakingProgram.coder.accounts.decode(
            "stakingPositions",
            accountInfo.account.data
          );
          positionsAccounts.push({
            publicKey: accountInfo.pubkey,
            account: decoded,
          });
        } catch {
          // Skip accounts that fail to decode (likely different account type)
          failedAccounts.push(accountInfo.pubkey.toBase58());
        }
      }

      if (this.resolvedOptions.output === "json") {
        this.output({
          accounts: positionsAccounts.map((acc) => ({
            publicKey: acc.publicKey.toBase58(),
            stakeAccountKey: acc.account.stakeAccountKey.toBase58(),
            totalCrnchStaked: acc.account.totalCrnchStaked.toString(),
            positionsCount: acc.account.positions.length,
            positions: acc.account.positions.map((p: any) => ({
              crnchStaked: p.crnchStaked.toString(),
              activationEpoch: p.activationEpoch.toString(),
              sharesAcquired: p.sharesAcquired.toString(),
              targetWithParameters: p.targetWithParameters,
            })),
          })),
          total: positionsAccounts.length,
          failed: failedAccounts.length,
        });
      } else {
        console.log(chalk.cyan.bold("\n📋 All Staking Positions Accounts"));
        console.log(chalk.gray("═".repeat(120)));
        console.log(`Total StakingPositions accounts: ${positionsAccounts.length}`);
        if (failedAccounts.length > 0) {
          console.log(
            chalk.yellow(
              `⚠️  Skipped ${failedAccounts.length} accounts (not StakingPositions or decode failed)`
            )
          );
        }
        console.log();

        if (positionsAccounts.length === 0) {
          console.log(chalk.gray("No StakingPositions accounts found.\n"));
          return;
        }

        const table = createTable({
          colWidths: [50, 50, 20, 15],
        });

        table.push([
          chalk.yellow("Account Address"),
          chalk.yellow("Stake Account"),
          chalk.yellow("Total Staked"),
          chalk.yellow("Positions"),
        ]);

        positionsAccounts.forEach((account) => {
          table.push([
            chalk.white(account.publicKey.toBase58()),
            chalk.cyan(account.account.stakeAccountKey.toBase58()),
            chalk.green(formatCrunch(account.account.totalCrnchStaked)),
            chalk.gray(account.account.positions.length.toString()),
          ]);
        });

        console.log(table.toString());
        console.log();

        // Show summary statistics
        const totalStaked = positionsAccounts.reduce(
          (sum, acc) => sum + Number(acc.account.totalCrnchStaked),
          0
        );
        const totalPositions = positionsAccounts.reduce(
          (sum, acc) => sum + acc.account.positions.length,
          0
        );

        console.log(chalk.cyan.bold("📊 Summary"));
        console.log(chalk.gray("─".repeat(80)));
        console.log(`Total CRNCH Staked: ${chalk.green(formatCrunch(totalStaked))} CRNCH`);
        console.log(`Total Positions: ${chalk.white(totalPositions)}`);
        console.log(
          `Average Positions per Account: ${chalk.white(
            (totalPositions / positionsAccounts.length).toFixed(2)
          )}`
        );
        console.log();
      }
    } catch (error) {
      console.error("❌ Error listing StakingPositions accounts:", error);
      throw error;
    }
  }
}
